import React, { useCallback, useEffect, useState } from "react";
import { AlertCircle, Download, RefreshCw, TrendingDown, TrendingUp } from "lucide-react";

import api from "../api";
import { printReport } from "./reportPrint";

function amount(value) {
  return `Rs. ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function currentFyStart() {
  const today = new Date();
  const year = today.getMonth() + 1 >= 4 ? today.getFullYear() : today.getFullYear() - 1;
  return `${year}-04-01`;
}

function ProfitLoss() {
  const today = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(currentFyStart());
  const [dateTo, setDateTo] = useState(today);
  const [report, setReport] = useState({ revenue: { items: [], groups: [], total: 0 }, expenses: { items: [], groups: [], total: 0 }, net_profit: 0, margin_pct: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadReport = useCallback(async (from = dateFrom, to = dateTo) => {
    setLoading(true);
    try {
      const response = await api.get(`/accounts/reports/profit-loss?date_from=${from}&date_to=${to}`);
      setReport(response.data || {});
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load profit and loss report.");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    loadReport(dateFrom, dateTo);
  }, [dateFrom, dateTo, loadReport]);

  const downloadPdf = () => {
    printReport({
      title: "Profit & Loss",
      subtitle: `${report.period?.from || dateFrom} to ${report.period?.to || dateTo}`,
      summary: [
        { label: "Revenue", value: amount(report.revenue?.total) },
        { label: "Expenses", value: amount(report.expenses?.total) },
        { label: "Net Profit", value: amount(report.net_profit) },
        { label: "Margin", value: `${Number(report.margin_pct || 0).toLocaleString()}%` },
      ],
      tables: [
        ...((report.revenue?.groups || []).map((group) => ({
          title: `Revenue - ${group.category}`,
          columns: ["Code", "Account", "Amount"],
          rows: (group.items || []).map((row) => [row.code, row.name, amount(row.amount)]),
        }))),
        ...((report.expenses?.groups || []).map((group) => ({
          title: `Expenses - ${group.category}`,
          columns: ["Code", "Account", "Amount"],
          rows: (group.items || []).map((row) => [row.code, row.name, amount(row.amount)]),
        }))),
      ],
    });
  };

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-5">
          <div>
            <h3 className="text-xl font-bold text-slate-900">Profit & Loss</h3>
            <p className="text-sm text-slate-500">Revenue minus expenses grouped by account category for the selected period.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} className="input" />
            <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} className="input" />
            <button type="button" onClick={() => loadReport(dateFrom, dateTo)} className="secondary-btn">
              <RefreshCw size={15} />
              Generate
            </button>
            <button type="button" onClick={downloadPdf} className="secondary-btn">
              <Download size={15} />
              Download as PDF
            </button>
          </div>
        </div>

        {error && <ErrorBanner message={error} />}

        <div className="grid gap-4 px-6 py-5 md:grid-cols-4">
          <MetricCard label="Revenue" value={report.revenue?.total} tone="emerald" icon={TrendingUp} />
          <MetricCard label="Expenses" value={report.expenses?.total} tone="rose" icon={TrendingDown} />
          <MetricCard label="Net Profit" value={report.net_profit} tone={report.net_profit >= 0 ? "blue" : "amber"} icon={TrendingUp} />
          <MetricCard label="Margin" value={`${Number(report.margin_pct || 0).toLocaleString()}%`} tone="indigo" icon={TrendingUp} isCurrency={false} />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <GroupedSection title="Revenue" groups={report.revenue?.groups} loading={loading} emptyText="No revenue balances found for the selected range." />
        <GroupedSection title="Expenses" groups={report.expenses?.groups} loading={loading} emptyText="No expense balances found for the selected range." />
      </div>

      <style>{`
        .input {
          border-radius: 0.75rem;
          border: 1px solid rgb(203 213 225);
          padding: 0.65rem 0.85rem;
          background: white;
        }
        .secondary-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          border-radius: 0.75rem;
          padding: 0.65rem 1rem;
          font-size: 0.875rem;
          font-weight: 600;
          border: 1px solid rgb(203 213 225);
          background: white;
          color: rgb(51 65 85);
        }
      `}</style>
    </div>
  );
}

function MetricCard({ label, value, icon: Icon, tone = "blue", isCurrency = true }) {
  const toneMap = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    rose: "border-rose-200 bg-rose-50 text-rose-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    indigo: "border-indigo-200 bg-indigo-50 text-indigo-700",
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-11 w-11 items-center justify-center rounded-2xl border ${toneMap[tone]}`}>
          <Icon size={20} />
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
          <div className="text-2xl font-bold text-slate-900">{isCurrency ? amount(value) : value}</div>
        </div>
      </div>
    </div>
  );
}

function GroupedSection({ title, groups = [], loading, emptyText }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-6 py-5">
        <h4 className="text-lg font-bold text-slate-900">{title}</h4>
      </div>
      <div className="space-y-5 p-6">
        {!loading && groups.map((group) => (
          <section key={`${title}-${group.category}`} className="rounded-2xl border border-slate-200">
            <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-4 py-3">
              <div className="font-semibold text-slate-900">{group.category}</div>
              <div className="text-sm font-semibold text-slate-700">{amount(group.total)}</div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Code</th>
                    <th className="px-4 py-3">Account</th>
                    <th className="px-4 py-3">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(group.items || []).map((row) => (
                    <tr key={`${title}-${group.category}-${row.code}`} className="border-t border-slate-100">
                      <td className="px-4 py-3 font-semibold text-indigo-700">{row.code}</td>
                      <td className="px-4 py-3">{row.name}</td>
                      <td className="px-4 py-3 font-semibold">{amount(row.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
        {!loading && !groups.length && <div className="py-10 text-center text-slate-500">{emptyText}</div>}
        {loading && <div className="py-10 text-center text-slate-500">Loading report...</div>}
      </div>
    </div>
  );
}

function ErrorBanner({ message }) {
  return (
    <div className="mx-6 mt-4 flex items-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
      <AlertCircle size={16} />
      <span>{message}</span>
    </div>
  );
}

export default ProfitLoss;
